import { ArrowLeft, Calendar } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const MicroDiversityDetail = () => {
  const navigate = useNavigate();

  const dailyColors = [
    { color: "Red", count: 3 },
    { color: "Orange", count: 2 },
    { color: "Yellow", count: 2 },
    { color: "Green", count: 4 },
    { color: "Blue/Purple", count: 2 },
    { color: "White", count: 3 },
  ];

  const weeklyData = [
    { day: "Mon", score: 16 },
    { day: "Tue", score: 18 },
    { day: "Wed", score: 15 },
    { day: "Thu", score: 19 },
    { day: "Fri", score: 20 },
    { day: "Sat", score: 17 },
    { day: "Sun", score: 18 },
  ];

  const monthlyData = [
    { week: "Week 1", score: 16 },
    { week: "Week 2", score: 18 },
    { week: "Week 3", score: 19 },
    { week: "Week 4", score: 18 },
  ];

  const colorMapping: Record<string, string> = {
    Red: "#ef4444",
    Orange: "#f97316",
    Yellow: "#eab308",
    Green: "#22c55e",
    "Blue/Purple": "#8b5cf6",
    White: "#94a3b8",
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Micro Diversity</h1>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl"
            onClick={() => {
              // TODO: Implement calendar functionality
            }}
          >
            <Calendar className="h-5 w-5" />
          </Button>
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-chart-3/10 to-chart-3/5 p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-subhead text-muted-foreground">
              Today's Micro Diversity
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">18</div>
            <p className="text-subhead text-muted-foreground">
              of 30 plant varieties
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-chart-3"
                style={{ width: "60%" }}
              />
            </div>
          </div>
        </Card>

        {/* Color Diversity Today */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-foreground">
            Today's Food Colors
          </h3>
          <div className="space-y-3">
            {dailyColors.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="h-6 w-6 rounded-full border-2 border-border"
                    style={{ backgroundColor: colorMapping[item.color] }}
                  />
                  <span className="text-subhead font-medium">{item.color}</span>
                </div>
                <span className="text-subhead text-muted-foreground">
                  {item.count} items
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* What is Micro Diversity */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Micro Diversity Score?
          </h3>
          <p className="text-subhead leading-relaxed text-muted-foreground">
            Micro Diversity measures the variety of plant-based foods you
            consume, tracking different colors and prebiotic sources. Research
            shows eating 30+ different plant varieties per week significantly
            improves gut microbiome diversity, which is linked to better immune
            function, digestion, and overall health.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-success font-semibold">
                25-30 varieties
              </span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">20-24 varieties</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Fair</span>
              <span className="text-warning font-semibold">
                15-19 varieties
              </span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">Below 15</span>
            </div>
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-2 p-1">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    tick={CHART_AXIS_TICK_LG}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={CHART_AXIS_TICK_LG}
                    domain={[0, 30]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-3))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    tick={CHART_AXIS_TICK_LG}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={CHART_AXIS_TICK_LG}
                    domain={[0, 30]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    fill="hsl(var(--chart-3))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Ways to Increase Diversity
          </h3>
          <ul className="space-y-2 text-subhead text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-3">•</span>
              <span>Try a new vegetable or fruit each week</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-3">•</span>
              <span>Eat a rainbow - aim for different colored foods daily</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-3">•</span>
              <span>Include herbs and spices (they count as plants!)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-3">•</span>
              <span>Rotate your grains, legumes, nuts, and seeds</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default MicroDiversityDetail;
